import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { StringFormatExtention } from "by/gamefactory/extensions/StringFormatExtention";
import { Event } from "event-system";
import { gsap } from "gsap";
import { DropShadowFilter } from "pixi-filters";
import { Container, FederatedPointerEvent, Point, Sprite, Text, TextStyle, Texture } from "pixi.js";
import { GameFont } from "../../../common/GameFont";
import { Avatar } from "../../../common/components/Avatar";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";
import { HBox } from "../../../common/components/ui/HBox";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { UserProfileResourcePackage } from "../../userprofile/resources/UserProfileResourcePackage";
import { BuildingRankUpResourcePackage } from "../resources/BuildingRankUpResourcePackage";
import { LandlordGlowRotationAnimation } from "./LandlordGlowRotationAnimation";
import { ShineRenterAnimation } from "./ShineRenterAnimation";

export class BuildingRankUpRenterRewardComponent extends Container {
    private static readonly YOU_WON_TEXT = 'YOU WON';

    private landlordIcon: Sprite;
    private landlordAvatar: Avatar;
    private landlordInfoButton: ButtonWithScaleAnimation;
    private landlordRewardBox: HBox;
    private landlordRewardCount: Text;
    private landlordRewardIcon: Sprite;
    private landlordGlowAnimation: LandlordGlowRotationAnimation;

    private greenLight: Sprite;

    private youWonText: Text;
    private renterAvatar: Avatar;
    private renterInfoButton: ButtonWithScaleAnimation;
    private renterRewardBox: HBox;
    private renterRewardCount: Text;
    private renterRewardIcon: Sprite;
    private shineRenterAnimation: ShineRenterAnimation;

    private rewardGoldFilter: DropShadowFilter;

    public get onRenterInfoButtonClick(): Event<ButtonWithScaleAnimation, FederatedPointerEvent> {
        return this.renterInfoButton.onUp;
    }
    public get onLandlordInfoButtonClick(): Event<ButtonWithScaleAnimation, FederatedPointerEvent> {
        return this.landlordInfoButton.onUp;
    }

    public constructor(
        rankUpResources: BuildingRankUpResourcePackage,
        userProfileResources: UserProfileResourcePackage,
        renterAvatarTexture: Texture,
        landlordAvatarTexture: Texture,
        landlordRewardCount: number,
        renterRewardCount: number,
        playerModel: IPlayerModel,
        canvasScaler: ICanvasScaler
    ) {
        super();
        this.rewardGoldFilter = new DropShadowFilter({
            offset: new Point(0, 0),
            blur: 30,
            color: 0xFFC700,
            quality: 4.5,
            alpha: 1,
            pixelSize: 0.6
        });

        this.landlordIcon = new Sprite(rankUpResources.assets_views_building_rank_up_landlord_icon_png);
        this.landlordInfoButton = new ButtonWithScaleAnimation(rankUpResources.assets_views_building_rank_up_info_icon_png);
        this.landlordAvatar = new Avatar(userProfileResources, landlordAvatarTexture);
        this.landlordAvatar.avatarSize = new Point(180, 181);
        this.landlordAvatar.glow = true;
        this.landlordAvatar.enabled = false;
        this.landlordRewardBox = new HBox(15);
        this.landlordRewardCount = new Text(StringFormatExtention.formatWithComma(landlordRewardCount), new RewardLandlordTextStyle());
        this.landlordRewardIcon = new Sprite(rankUpResources.assets_views_building_rank_up_coin_png);
        this.landlordRewardIcon.width = this.landlordRewardIcon.height = 64;
        this.landlordRewardBox.filters = [this.rewardGoldFilter];
        this.landlordRewardBox.addChild(this.landlordRewardCount, this.landlordRewardIcon);
        this.landlordGlowAnimation = new LandlordGlowRotationAnimation(canvasScaler);

        this.greenLight = new Sprite(rankUpResources.assets_views_building_rank_up_green_light_png);

        this.youWonText = new Text(BuildingRankUpRenterRewardComponent.YOU_WON_TEXT, new YouWonTextStyle());
        this.renterAvatar = new Avatar(userProfileResources, renterAvatarTexture);
        this.renterAvatar.avatarSize = new Point(120, 121);
        this.renterAvatar.glow = playerModel.isAuthorized;
        this.renterAvatar.enabled = false;
        this.renterInfoButton = new ButtonWithScaleAnimation(rankUpResources.assets_views_building_rank_up_info_icon_png);
        this.renterRewardBox = new HBox(10);
        this.renterRewardCount = new Text(StringFormatExtention.formatWithComma(renterRewardCount), new RewardRenterTextStyle());
        this.renterRewardIcon = new Sprite(rankUpResources.assets_views_building_rank_up_coin_png);
        this.renterRewardIcon.width = this.renterRewardIcon.height = 40;
        this.renterRewardBox.filters = [this.rewardGoldFilter];
        this.renterRewardBox.addChild(this.renterRewardCount, this.renterRewardIcon);
        this.shineRenterAnimation = new ShineRenterAnimation(canvasScaler);
        this.shineRenterAnimation.visible = playerModel.isAuthorized;

        this.addChild(
            this.landlordIcon,
            this.landlordAvatar,
            this.landlordGlowAnimation,
            this.landlordInfoButton,
            this.landlordRewardBox,
            this.greenLight,
            this.youWonText,
            this.renterAvatar,
            this.shineRenterAnimation,
            this.renterInfoButton,
            this.renterRewardBox
        );
        this.landlordInfoButton.visible = false;
        this.renterInfoButton.visible = false;
        this.layout();
    }

    private layout(): void {
        this.landlordIcon.position.set(6, 13);
        this.landlordInfoButton.position.set(-32, 83);
        this.landlordAvatar.position.set(0, 84);
        this.landlordRewardBox.position.set(this.landlordAvatar.position.x + this.landlordAvatar.width * 0.5 - this.landlordRewardBox.width * 0.5, 285);
        this.landlordRewardIcon.position.y = -9;
        this.landlordGlowAnimation.position.set(this.landlordAvatar.x + this.landlordAvatar.width * 0.5 - 3, -15);
        this.greenLight.position.set(206, 0);
        this.youWonText.position.set(259, 8);
        this.renterInfoButton.position.set(248, 83);
        this.renterAvatar.position.set(284, 83);
        this.shineRenterAnimation.scale.set(1.48, 0.32);
        this.shineRenterAnimation.position.set(this.renterAvatar.x + this.renterAvatar.width * 0.47, this.renterAvatar.y + this.renterAvatar.height * 0.47);
        this.renterRewardBox.position.set(this.renterAvatar.position.x + this.renterAvatar.width * 0.5 - this.renterRewardBox.width * 0.5, 220);
        this.renterRewardIcon.position.y = -5;
    }

    public async playAvatarsAnimations(): Promise<void> {
        await this.landlordGlowAnimation.play();
        await this.shineRenterAnimation.play();
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.delay(1);
        animationTimeLine.fromTo(this.landlordInfoButton, { alpha: 0 }, {
            alpha: 1, duration: 0.5, onStart: () => {
                this.landlordInfoButton.visible = true;
                this.renterInfoButton.visible = true;
            }
        });
        animationTimeLine.fromTo(this.renterInfoButton, { alpha: 0 }, { alpha: 1, duration: 0.5 }, ">");
        await animationTimeLine.play();
        animationTimeLine.kill();
    }

    public hide(): void {
        this.landlordGlowAnimation.visible = false;
        this.shineRenterAnimation.visible = false;
    }
}

class YouWonTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 64,
            fill: '#FFFFFF'
        })
    }
}

class RewardRenterTextStyle extends YouWonTextStyle {
    public constructor() {
        super();
        this.fontSize = 36;
    }
}

class RewardLandlordTextStyle extends YouWonTextStyle {
    public constructor() {
        super();
        this.fontSize = 57;
    }
}
